/***
 * This file contains general logic for assessing authorization as part of FDC3 interop.
 *
 * For example, if App A broadcasts a context, but App A has authorization rules that only trust App B,
 * App A's context should only make it to App B. This file provides the logic to process those authorization rules.
 *
 *
 * * Authorization rules require that a specific contextType is provided in each rule. ContextType should be optional (KB: #35932)
 * * Consider slimming down these functions. There is a lot of potential DRY violations here that could be reduced.
 */

import { InteropStore, SelectConnectAppList, SelectConnectRule } from "../types";

type LimitOptionType = {
	contextType?: string;
	intentType?: string;
};

export const isInteropAuthorized = (appName: string, rules: SelectConnectAppList[]) =>
	rules.some((rule) => rule.name === appName);

const isRulesetRelevant = (ruleSet: SelectConnectRule, limitOptions: LimitOptionType) => {
	// If this rule is restricted by contextType, make sure the limitOptions are about the right contextType
	if (ruleSet.contextType && !(limitOptions.contextType && ruleSet.contextType === limitOptions.contextType)) {
		return false;
	}
	// If this rule is restricted by intentType, make sure the limitOptions are about the right intentType
	return !(ruleSet.intentType && !(limitOptions.intentType && ruleSet.intentType === limitOptions.intentType));
};

// Build the limit options object, based on what information was provided when this function was invoked
// For example, if a context was being broadcast, only rules related to context limits are relevant, so context limiters
// are all that will be checked.
const generateLimitOptions = (contextType?: string, intentType?: string) => {
	return {
		...(contextType ? { contextType } : {}),
		...(intentType ? { intentType } : {}),
	};
};

const runChecks = (
	selectConnect: SelectConnectRule[],
	limitOptions: LimitOptionType,
	authorizationCheckCallback: (rule: SelectConnectAppList[]) => string[] | boolean,
	isDestinationCheck: boolean,
	defaultValue: any
) => {
	// Find the relevant selectConnect rules
	for (const ruleSet of selectConnect) {
		// If this rule isn't about the right context or intent, skip it
		if (!isRulesetRelevant(ruleSet, limitOptions)) {
			continue;
		}
		const { authorize, to, from } = ruleSet;

		if (isDestinationCheck) {
			if (from) {
				return authorizationCheckCallback(from);
			}
		} else {
			if (to) {
				return authorizationCheckCallback(to);
			}
		}

		if (authorize) {
			return authorizationCheckCallback(authorize);
		}
	}

	return defaultValue;
};

type FilterForAuthorizationProps = {
	idList: string[];
	contextType?: string;
	intentType?: string;
	selectConnect: SelectConnectRule[];
	getRules: (uuid: string) => SelectConnectRule[];
	getAppName: (app: string) => string;
	appName: string;
	authenticated: boolean;
	checkDestinationAuthentication: (id: string) => boolean;
};

const filterForAuthorization = ({
	idList,
	contextType,
	intentType,
	selectConnect,
	getRules,
	getAppName,
	appName,
	authenticated,
	checkDestinationAuthentication,
}: FilterForAuthorizationProps) => {
	let returnList: string[] = idList;

	// Determine what limit options apply
	const limitOptions = generateLimitOptions(contextType, intentType);

	// Filter the list of destinations. Remove destinations that don't trust the originating app
	returnList = returnList.filter((uuid) =>
		runChecks(getRules(uuid), limitOptions, (rule) => isInteropAuthorized(appName, rule) && authenticated, true, true)
	);

	// Filter the list of destinations. Remove destinations that the originating app doesn't trust
	returnList = runChecks(
		selectConnect,
		limitOptions,
		(rule: SelectConnectAppList[]) =>
			returnList.filter((app) => isInteropAuthorized(getAppName(app), rule) && checkDestinationAuthentication(app)),
		false,
		returnList
	);

	return returnList;
};

type FilterForClientAuthorizationProps = {
	state: InteropStore;
	originClientUuid: string;
	destinationClientUuidList: string[];
	contextType?: string;
	intentType?: string;
};

/***
 * When sending data (usually broadcasting context) from an origin client to a list of possible destination clients,
 * the list of possible destinations need to be filtered based on possible authorization rules.
 * This function takes metadata and a list of possible destination UUID's, and returns a filtered version of that list,
 * where the unauthorized destinations have been removed.
 * Note: This is only for clients, which have UUIDs. For apps generally, see the function `filterForAppAuthorization`.
 *
 * @param {Object} options
 * @param {InteropStore} options.state The current InteropStore state collection (which contains authorization rules)
 * @param {String[]} options.destinationClientUuidList The list of the UUID's of the possible destinations
 * @param {String} options.originClientUuid The UUID of the origin client
 * @param {String} [options.contextType] The type of context to be assessed for
 * @param {String} [options.intentType] The type of intent to be assessed for
 *
 * @returns {String[]} The list of the UUID's of the *filtered* destinations
 */
export const filterForClientAuthorization = ({
	state,
	destinationClientUuidList,
	originClientUuid,
	contextType,
	intentType,
}: FilterForClientAuthorizationProps) => {
	const { appName, authenticated } = state.resolver.clients[originClientUuid];
	const selectConnect = state.resolver.clients[originClientUuid]?.config?.interop?.selectConnect ?? [];
	const getRules = (uuid: string) => state.resolver.clients[uuid].config?.interop?.selectConnect ?? [];
	const getAppName = (app: string) => state.resolver.clients[app].appName;
	const checkDestinationAuthentication = (uuid: string) => state.resolver.clients[uuid].authenticated;

	return filterForAuthorization({
		appName,
		idList: destinationClientUuidList,
		contextType,
		intentType,
		selectConnect,
		getRules,
		getAppName,
		authenticated,
		checkDestinationAuthentication,
	});
};

type FilterForAppAuthorizationProps = {
	state: InteropStore;
	originClientUuid: string;
	destinationAppIdList: string[];
	contextType?: string;
	intentType?: string;
};

/***
 * When sending data (usually raising intents) from an origin client to a list of possible destination apps,
 * the list of possible destinations need to be filtered based on possible authorization rules.
 * This function takes metadata and a list of possible appId's, and returns a filtered version of that list,
 * where the unauthorized destinations have been removed.
 * Note: This is only for apps, which have appId's. For sending data to specific clients,
 * see the function `filterForClientAuthorization`.
 *
 * @param {Object} options
 * @param {InteropStore} options.state The current InteropStore state collection (which contains authorization rules)
 * @param {String[]} options.destinationAppIdList The list of the appId's of the possible destinations
 * @param {String} options.originClientUuid The UUID of the origin client
 * @param {String} [options.contextType] The type of context to be assessed for
 * @param {String} [options.intentType] The type of intent to be assessed for
 *
 * @returns {String[]} The list of the UUID's of the *filtered* destinations
 */
export const filterForAppAuthorization = ({
	state,
	destinationAppIdList,
	originClientUuid,
	contextType,
	intentType,
}: FilterForAppAuthorizationProps): String[] => {
	const { appName, authenticated } = state.resolver.clients[originClientUuid];
	const selectConnect = state.resolver.clients[originClientUuid].config?.interop?.selectConnect ?? [];
	const getRules = (uuid: string) =>
		state.resolver.apps.definitions[uuid]?.manifest?.interop?.selectConnect ?? ([] as SelectConnectRule[]);
	const getAppName = (app: string) => state.resolver.apps.definitions[app].name;

	// When the destination is an app, not a client, authentication isn't relevant
	const checkDestinationAuthentication = () => true;

	return filterForAuthorization({
		appName,
		idList: destinationAppIdList,
		contextType,
		intentType,
		selectConnect,
		getRules,
		getAppName,
		authenticated,
		checkDestinationAuthentication,
	});
};
